import { create } from 'zustand';
import { CapabilityCitation, CitationAuditResult } from '../types/capabilityCitation';
import { CAPABILITY_CITATIONS } from '../data/capabilityCitations';

interface CapabilityCitationStore {
  citations: CapabilityCitation[];
  getCitation: (capabilityId: string) => CapabilityCitation | undefined;
  getCitationsByType: (capabilityType: string) => CapabilityCitation[];
  searchCitations: (query: string) => CapabilityCitation[];
  getAuditReport: () => CitationAuditResult;
  addCitation: (citation: CapabilityCitation) => void;
  updateCitation: (capabilityId: string, updates: Partial<CapabilityCitation>) => void;
}

export const useCapabilityCitationStore = create<CapabilityCitationStore>((set, get) => ({
  citations: CAPABILITY_CITATIONS,

  getCitation: (capabilityId: string) => {
    return get().citations.find(c => c.capabilityId === capabilityId);
  },

  getCitationsByType: (capabilityType: string) => {
    return get().citations.filter(c => c.capabilityType === capabilityType);
  },

  searchCitations: (query: string) => {
    const lowerQuery = query.toLowerCase();
    return get().citations.filter(c => 
      c.capabilityName.toLowerCase().includes(lowerQuery) ||
      c.citationTitle.toLowerCase().includes(lowerQuery) ||
      c.summary.toLowerCase().includes(lowerQuery)
    );
  },

  getAuditReport: () => {
    const citations = get().citations;
    const totalCapabilities = citations.length; // In a real app, we'd compare against a master capability list
    const citedCapabilities = citations.filter(c => c.citationURL && c.citationURL.length > 0).length;
    const brokenLinks = citations.filter(c => !c.verified).length; // Simplified check
    
    // Check for outdated citations (older than 10 years)
    const currentYear = new Date().getFullYear();
    const outdatedCitations = citations.filter(c => {
      const pubYear = new Date(c.datePublished).getFullYear();
      return (currentYear - pubYear) > 10;
    }).length;

    return {
      totalCapabilities,
      citedCapabilities,
      brokenLinks,
      outdatedCitations,
      uncitedCapabilityIds: [] // Would need master list to populate this
    };
  },

  addCitation: (citation: CapabilityCitation) => {
    set(state => ({
      citations: [...state.citations, citation]
    }));
  },

  updateCitation: (capabilityId: string, updates: Partial<CapabilityCitation>) => {
    set(state => ({
      citations: state.citations.map(c => 
        c.capabilityId === capabilityId ? { ...c, ...updates } : c
      )
    }));
  }
}));
